import React, { useState, useEffect, useRef } from 'react';
import { collection, onSnapshot, doc, updateDoc, deleteDoc, query } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { Modal, InputField } from './SharedComponents';
import { CheckCircle, Clock, DollarSign, Edit, Trash2, AlertTriangle } from 'lucide-react';

const BuscarPagarMultas = ({ mode, userRole, isSuperAdmin }) => {
    const [multas, setMultas] = useState([]);
    const [infracciones, setInfracciones] = useState([]);
    const [filtroInfraccion, setFiltroInfraccion] = useState('');
    const [filtroGeneral, setFiltroGeneral] = useState('');
    const [filtroPropietario, setFiltroPropietario] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('todos');
    
    // Estados para modales
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [currentMulta, setCurrentMulta] = useState(null);
    const [selectedDiscount, setSelectedDiscount] = useState(0);
    const [numeroRecibo, setNumeroRecibo] = useState('');
    const [message, setMessage] = useState({ text: '', type: '' });

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingMulta, setEditingMulta] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [multaToDelete, setMultaToDelete] = useState(null);

    // Estados para la búsqueda de infracciones en el modal de edición
    const [infraccionSearch, setInfraccionSearch] = useState('');
    const [filteredInfracciones, setFilteredInfracciones] = useState([]);
    const [showInfraccionesDropdown, setShowInfraccionesDropdown] = useState(false);
    const infraccionDropdownRef = useRef(null);

    useEffect(() => {
        const path = `artifacts/${appId}/public/data/multas`;
        const q = query(collection(db, path));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const multasData = [];
            querySnapshot.forEach((doc) => {
                multasData.push({ id: doc.id, ...doc.data() });
            });
            setMultas(multasData.sort((a, b) => new Date(b.fecha_registro) - new Date(a.fecha_registro)));
        });

        const infraccionesPath = `artifacts/${appId}/public/data/tipos_infraccion`;
        const qInfracciones = query(collection(db, infraccionesPath));
        const unsubInfracciones = onSnapshot(qInfracciones, (snapshot) => {
            setInfracciones(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const handleClickOutside = (event) => {
            if (infraccionDropdownRef.current && !infraccionDropdownRef.current.contains(event.target)) {
                setShowInfraccionesDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            unsubscribe();
            unsubInfracciones();
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleOpenDiscountModal = (multa) => {
        setCurrentMulta(multa);
        setSelectedDiscount(0);
        setNumeroRecibo('');
        setShowDiscountModal(true);
    };
    
    const confirmPagar = async () => {
        if (!currentMulta || !numeroRecibo) return;
        const montoFinal = Number(currentMulta.monto) * (1 - selectedDiscount);
        try {
            const path = `artifacts/${appId}/public/data/multas`;
            const multaRef = doc(db, path, currentMulta.id);
            await updateDoc(multaRef, { 
                estado: 'pagada', 
                fecha_pago: new Date().toISOString(),
                descuento_aplicado: selectedDiscount * 100,
                monto_final: montoFinal,
                numero_recibo: numeroRecibo,
            });
            setMessage({ text: 'Pago registrado exitosamente.', type: 'success' });
        } catch (error) {
            setMessage({ text: 'Error al registrar el pago.', type: 'error' });
        } finally {
            setShowDiscountModal(false);
            setCurrentMulta(null);
        }
    };
    
    const handleEditClick = (multa) => {
        const currentInfraccion = infracciones.find(i => i.id === multa.tipo_infraccion_id);
        setInfraccionSearch(currentInfraccion ? `${currentInfraccion.codigo} - ${currentInfraccion.descripcion}` : '');
        setEditingMulta(multa);
        setIsEditModalOpen(true);
    };

    const handleDeleteClick = (multa) => {
        setMultaToDelete(multa);
        setIsDeleteModalOpen(true);
    };

    const handleUpdateMulta = async (e) => {
        e.preventDefault();
        if (!editingMulta) return;
        try {
            const path = `artifacts/${appId}/public/data/multas`;
            const docRef = doc(db, path, editingMulta.id);
            const { id, ...dataToUpdate } = editingMulta;
            await updateDoc(docRef, {
                ...dataToUpdate,
                monto: Number(dataToUpdate.monto)
            });
            setMessage({ text: 'Multa actualizada correctamente.', type: 'success' });
        } catch (error) {
            setMessage({ text: 'Error al actualizar la multa.', type: 'error' });
        } finally {
            setIsEditModalOpen(false);
            setEditingMulta(null);
        }
    };

    const confirmDelete = async () => {
        if (!multaToDelete) return;
        try {
            const path = `artifacts/${appId}/public/data/multas`;
            await deleteDoc(doc(db, path, multaToDelete.id));
            setMessage({ text: 'Multa eliminada correctamente.', type: 'success' });
        } catch (error) {
             setMessage({ text: 'Error al eliminar la multa.', type: 'error' });
        } finally {
            setIsDeleteModalOpen(false);
            setMultaToDelete(null);
        }
    };

    const handleEditingInfraccionSearchChange = (e) => {
        const searchTerm = e.target.value;
        setInfraccionSearch(searchTerm);

        if (searchTerm) {
            const filtered = infracciones.filter(inf => 
                inf.codigo.toLowerCase().includes(searchTerm.toLowerCase()) || 
                inf.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredInfracciones(filtered);
            setShowInfraccionesDropdown(true);
        } else {
            setFilteredInfracciones([]);
            setShowInfraccionesDropdown(false);
        }
    };

    const handleEditingInfraccionSelect = (infraccion) => {
        setInfraccionSearch(`${infraccion.codigo} - ${infraccion.descripcion}`);
        setEditingMulta(prev => ({
            ...prev,
            tipo_infraccion_id: infraccion.id,
            monto: infraccion.monto
        }));
        setShowInfraccionesDropdown(false);
    };

    const multasFiltradas = multas.filter(multa => {
        const busquedaInfraccion = filtroInfraccion.toLowerCase();
        const busquedaGeneral = filtroGeneral.toLowerCase();
        const busquedaPropietario = filtroPropietario.toLowerCase();

        const matchInfraccion = busquedaInfraccion === '' || (multa.numero_infraccion && multa.numero_infraccion.toLowerCase().includes(busquedaInfraccion));
        const matchGeneral = busquedaGeneral === '' || multa.ruc_dni.toLowerCase().includes(busquedaGeneral) || multa.razon_social_nombre.toLowerCase().includes(busquedaGeneral);
        const matchPropietario = busquedaPropietario === '' || (multa.propietario_nombre && multa.propietario_nombre.toLowerCase().includes(busquedaPropietario));
        const matchEstado = filtroEstado === 'todos' || multa.estado === filtroEstado;
        const matchMode = mode === 'pagar' ? multa.estado === 'pendiente' : true;
        
        return matchInfraccion && matchGeneral && matchPropietario && matchEstado && matchMode;
    });

    const getStatusChip = (estado) => {
        switch (estado) {
            case 'pagada': return <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full"><CheckCircle className="inline w-3 h-3 mr-1" />Pagada</span>;
            case 'pendiente': return <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-200 rounded-full"><Clock className="inline w-3 h-3 mr-1" />Pendiente</span>;
            default: return <span className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-200 rounded-full">{estado}</span>;
        }
    };
    
    const montoFinal = currentMulta ? Number(currentMulta.monto) * (1 - selectedDiscount) : 0;
    const isConfirmDisabled = !numeroRecibo;

    const filterGridClass = mode === 'buscar' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" : "grid grid-cols-1 md:grid-cols-3 gap-4";

    return (
        <div className="space-y-4">
            <Modal isOpen={showDiscountModal} onClose={() => setShowDiscountModal(false)}>
                <h3 className="text-xl font-bold mb-4">Procesar Pago</h3>
                {currentMulta && (
                    <div className="space-y-4">
                        <p><span className="font-semibold">Contribuyente:</span> {currentMulta.razon_social_nombre}</p>
                        <p><span className="font-semibold">Monto Original:</span> <span className="text-lg font-bold">S/ {Number(currentMulta.monto).toFixed(2)}</span></p>
                        <div className="border-t pt-4">
                            <p className="font-semibold mb-2">Seleccione un descuento:</p>
                            <div className="flex flex-wrap gap-2">
                                {[0.8, 0.7, 0.5, 0.3, 0.15, 0].map(desc => (
                                    <button 
                                      key={desc} 
                                      onClick={() => setSelectedDiscount(desc)}
                                      className={`px-3 py-1 rounded-md text-sm transition-colors ${selectedDiscount === desc ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                    >
                                        {desc === 0 ? 'Sin Descuento' : `${desc * 100}% Desc.`}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <InputField
                            label="Número de Recibo"
                            value={numeroRecibo}
                            onChange={(e) => setNumeroRecibo(e.target.value)}
                            required
                            placeholder="Ingrese el N° de recibo"
                        />
                        <div className="bg-green-100 p-4 rounded-md text-center">
                            <p className="text-gray-600">MONTO FINAL A PAGAR</p>
                            <p className="text-3xl font-bold text-green-700">S/ {montoFinal.toFixed(2)}</p>
                        </div>
                         <div className="mt-6 flex justify-end gap-3">
                            <button onClick={() => setShowDiscountModal(false)} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                            <button 
                                onClick={confirmPagar} 
                                disabled={isConfirmDisabled}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed"
                                title={isConfirmDisabled ? "Debe ingresar un número de recibo" : ""}
                            >
                                Confirmar Pago
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
            
            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
                <h3 className="text-xl font-semibold mb-4">Editar Multa</h3>
                {editingMulta && (
                    <form onSubmit={handleUpdateMulta} className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                        <InputField label="N° Infracción" value={editingMulta.numero_infraccion || ''} onChange={(e) => setEditingMulta({...editingMulta, numero_infraccion: e.target.value})} />
                        <InputField label="RUC/DNI Infractor" value={editingMulta.ruc_dni || ''} onChange={(e) => setEditingMulta({...editingMulta, ruc_dni: e.target.value})} />
                        <InputField label="Razón Social" value={editingMulta.razon_social_nombre || ''} onChange={(e) => setEditingMulta({...editingMulta, razon_social_nombre: e.target.value})} />
                        <InputField label="Establecimiento" value={editingMulta.nombre_establecimiento || ''} onChange={(e) => setEditingMulta({...editingMulta, nombre_establecimiento: e.target.value})} />
                        <InputField label="DNI Propietario" value={editingMulta.propietario_dni || ''} onChange={(e) => setEditingMulta({...editingMulta, propietario_dni: e.target.value})} />
                        <InputField label="Nombre Propietario" value={editingMulta.propietario_nombre || ''} onChange={(e) => setEditingMulta({...editingMulta, propietario_nombre: e.target.value})} />
                        <div ref={infraccionDropdownRef}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Infracción</label>
                            <div className="relative">
                                <AlertTriangle className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    value={infraccionSearch}
                                    onChange={handleEditingInfraccionSearchChange}
                                    onFocus={() => { if (infraccionSearch) setShowInfraccionesDropdown(true) }}
                                    placeholder="Buscar por código o descripción..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm"
                                    autoComplete="off"
                                />
                                 {showInfraccionesDropdown && filteredInfracciones.length > 0 && (
                                    <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                                        {filteredInfracciones.map(inf => (
                                            <li key={inf.id} onClick={() => handleEditingInfraccionSelect(inf)} className="px-4 py-2 cursor-pointer hover:bg-gray-100">
                                                <strong className="text-blue-600">{inf.codigo}</strong> - {inf.descripcion}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                        <InputField label="Monto" type="number" value={editingMulta.monto || 0} onChange={(e) => setEditingMulta({...editingMulta, monto: e.target.value})} />
                        <div className="flex justify-end gap-3 mt-6">
                            <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Guardar Cambios</button>
                        </div>
                    </form>
                )}
            </Modal>
            
            <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                 <h3 className="text-xl font-semibold mb-4">Confirmar Eliminación</h3>
                 <p>¿Está seguro de que desea eliminar la multa N° <strong>{multaToDelete?.numero_infraccion}</strong>? Esta acción no se puede deshacer.</p>
                 <div className="flex justify-end gap-3 mt-6">
                    <button type="button" onClick={() => setIsDeleteModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                    <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">Eliminar Multa</button>
                </div>
            </Modal>

            {message.text && (<div className={`p-4 mb-4 rounded-md ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{message.text}</div>)}
            
            <div className={filterGridClass}>
                 <input type="text" placeholder="Buscar por N° de Infracción..." value={filtroInfraccion} onChange={(e) => setFiltroInfraccion(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                <input type="text" placeholder="Buscar por RUC/DNI o Razón Social..." value={filtroGeneral} onChange={(e) => setFiltroGeneral(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                <input type="text" placeholder="Buscar por Nombre del Propietario..." value={filtroPropietario} onChange={(e) => setFiltroPropietario(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                 {mode === 'buscar' && (<select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md"><option value="todos">Todos los estados</option><option value="pendiente">Pendiente</option><option value="pagada">Pagada</option></select>)}
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° Infracción</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RUC/DNI</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razón Social</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Establecimiento</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Propietario</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 border-l w-24">Acción</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {multasFiltradas.map(multa => (
                            <tr key={multa.id}>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{new Date(multa.fecha_infraccion).toLocaleDateString('es-PE')}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-800">{multa.numero_infraccion || 'N/A'}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{multa.ruc_dni}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{multa.razon_social_nombre}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{multa.nombre_establecimiento || 'N/A'}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{multa.propietario_nombre || 'N/A'}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">S/ {Number(multa.monto).toFixed(2)}</td>
                                <td className="px-4 py-2 whitespace-nowrap">{getStatusChip(multa.estado)}</td>
                                <td className="px-4 py-2 whitespace-nowrap sticky right-0 bg-white border-l">
                                    <div className="flex items-center justify-center gap-2">
                                        {mode === 'pagar' && multa.estado === 'pendiente' && (
                                            <button onClick={() => handleOpenDiscountModal(multa)} className="p-1 text-green-600 hover:text-green-800" title="Pagar Multa">
                                                <DollarSign size={16}/>
                                            </button>
                                        )}
                                        {userRole === 'admin' && (
                                            <button onClick={() => handleEditClick(multa)} className="p-1 text-blue-600 hover:text-blue-800" title="Editar Multa">
                                                <Edit size={16}/>
                                            </button>
                                        )}
                                        {isSuperAdmin && (
                                            <button onClick={() => handleDeleteClick(multa)} className="p-1 text-red-600 hover:text-red-800" title="Eliminar Multa">
                                                <Trash2 size={16}/>
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BuscarPagarMultas;
